/**
 * US equity regular-session hours (NYSE / NASDAQ RTH).
 *
 * Open: Monday–Friday, 09:30–16:00 America/New_York.
 * Does not include pre/post market, early closes, or exchange holidays
 * unless listed in US_EQUITY_HOLIDAYS_YYYYMMDD (optional static set).
 */

export type UsEquitySessionState = {
  /** True when regular session is open */
  isOpen: boolean
  /** Short status for instrument chrome */
  label: 'US open' | 'US closed'
  /** Tooltip — honest scope */
  title: string
  /** Minutes since midnight ET (for tests / debug) */
  etMinutes: number
  /** 0=Sun … 6=Sat in America/New_York */
  etWeekday: number
}

/** Major US equity full-day closures (YYYY-MM-DD, America/New_York calendar date). Extend yearly. */
const US_EQUITY_HOLIDAYS = new Set([
  // 2026
  '2026-01-01', // New Year's Day
  '2026-01-19', // MLK Day
  '2026-02-16', // Presidents' Day
  '2026-04-03', // Good Friday
  '2026-05-25', // Memorial Day
  '2026-06-19', // Juneteenth
  '2026-07-03', // Independence Day (observed)
  '2026-09-07', // Labor Day
  '2026-11-26', // Thanksgiving
  '2026-12-25', // Christmas
  // 2025 (if still relevant)
  '2025-01-01',
  '2025-01-20',
  '2025-02-17',
  '2025-04-18',
  '2025-05-26',
  '2025-06-19',
  '2025-07-04',
  '2025-09-01',
  '2025-11-27',
  '2025-12-25',
])

function etParts(date: Date): {
  year: number
  month: number
  day: number
  weekday: number
  hour: number
  minute: number
} {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  const map = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value])
  ) as Record<string, string>

  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  }

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    weekday: weekdayMap[map.weekday ?? ''] ?? 0,
    hour: Number(map.hour),
    minute: Number(map.minute),
  }
}

function etDateKey(p: { year: number; month: number; day: number }): string {
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

/**
 * US equities regular trading hours status for `date` (default: now).
 */
export function getUsEquitySessionState(date: Date = new Date()): UsEquitySessionState {
  const p = etParts(date)
  const etMinutes = p.hour * 60 + p.minute
  const openStart = 9 * 60 + 30 // 09:30
  const openEnd = 16 * 60 // 16:00 (closed at 16:00 sharp)
  const isWeekday = p.weekday >= 1 && p.weekday <= 5
  const isHoliday = US_EQUITY_HOLIDAYS.has(etDateKey(p))
  const inHours = etMinutes >= openStart && etMinutes < openEnd
  const isOpen = isWeekday && !isHoliday && inHours

  const scope =
    'US equities RTH (NYSE/NASDAQ): Mon–Fri 09:30–16:00 ET. Holidays listed; early closes not modeled.'

  if (isOpen) {
    return {
      isOpen: true,
      label: 'US open',
      title: `Stock market open · ${scope}`,
      etMinutes,
      etWeekday: p.weekday,
    }
  }

  let reason = 'outside regular hours'
  if (!isWeekday) reason = 'weekend'
  else if (isHoliday) reason = 'exchange holiday'
  else if (etMinutes < openStart) reason = 'before open (09:30 ET)'
  else reason = 'after close (16:00 ET)'

  return {
    isOpen: false,
    label: 'US closed',
    title: `Stock market closed (${reason}) · ${scope}`,
    etMinutes,
    etWeekday: p.weekday,
  }
}
